/**
 * MileageSection — trajets affaires du client (Batch #14.243).
 */
import { useState } from "react";
import axios from "axios";
import { Car, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, portalHeaders } from "../helpers.js";
import { Field, SectionCard, inputCls, isDemoResponse, today, addBtnCls, formActionsCls, cancelBtnCls, saveBtnCls, deleteBtnCls } from "./_shared";

const EMPTY = { trip_date: today(), from_location: "", to_location: "", purpose: "", km_distance: "" };

function TripForm({ primaryColor, onDone, onCancel }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async () => {
    if (!f.trip_date || !f.from_location.trim() || !f.to_location.trim() || !(Number(f.km_distance) > 0)) {
      toast.error("Date, départ, destination et distance requis"); return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/portal/entrepreneur/mileage`, { ...f, km_distance: Number(f.km_distance) }, { headers: portalHeaders() });
      if (!isDemoResponse(res)) toast.success("Trajet enregistré");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="border-2 rounded-2xl p-4 space-y-3 bg-gray-50/60" style={{ borderColor: primaryColor }} data-testid="ent-portal-mileage-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Date *"><input type="date" className={inputCls} value={f.trip_date} onChange={(e) => set("trip_date", e.target.value)} data-testid="ent-portal-km-date" /></Field>
        <Field label="Distance (km) *"><input type="number" inputMode="decimal" step="0.1" min="0" className={inputCls} value={f.km_distance} onChange={(e) => set("km_distance", e.target.value)} placeholder="0" data-testid="ent-portal-km-distance" /></Field>
        <Field label="Départ *"><input className={inputCls} value={f.from_location} onChange={(e) => set("from_location", e.target.value)} placeholder="Ex. : Bureau" data-testid="ent-portal-km-from" /></Field>
        <Field label="Destination *"><input className={inputCls} value={f.to_location} onChange={(e) => set("to_location", e.target.value)} placeholder="Ex. : Client à Laval" data-testid="ent-portal-km-to" /></Field>
        <Field label="Motif du déplacement" className="sm:col-span-2"><input className={inputCls} value={f.purpose} onChange={(e) => set("purpose", e.target.value)} placeholder="Rencontre client, livraison, achat de matériel…" data-testid="ent-portal-km-purpose" /></Field>
      </div>
      <div className={formActionsCls}>
        <button type="button" onClick={onCancel} className={cancelBtnCls}>Annuler</button>
        <button type="button" onClick={submit} disabled={saving} className={saveBtnCls} style={{ backgroundColor: primaryColor }} data-testid="ent-portal-km-save">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer le trajet
        </button>
      </div>
    </div>
  );
}

export default function MileageSection({ data, primaryColor, onChanged }) {
  const [adding, setAdding] = useState(false);
  const remove = async (k) => {
    if (!window.confirm(`Supprimer ce trajet de ${k.km_distance} km ?`)) return;
    try {
      const res = await axios.delete(`${API}/portal/entrepreneur/mileage/${k.id}`, { headers: portalHeaders() });
      if (!isDemoResponse(res)) toast.success("Trajet supprimé");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <SectionCard title={`Mon kilométrage ${data.year}`} icon={Car} primaryColor={primaryColor} testId="ent-portal-mileage"
      action={!adding && (
        <button onClick={() => setAdding(true)} className={addBtnCls} style={{ backgroundColor: primaryColor }} data-testid="ent-portal-km-add-btn">
          <Plus className="w-4 h-4" /> Ajouter un trajet
        </button>
      )}>
      <p className="text-xs text-gray-500 mb-3">Total : <strong data-testid="ent-portal-km-total">{Number(data.totals.km || 0).toLocaleString("fr-CA")} km</strong> · {data.totals.nb_mileage} trajet(s)</p>
      {adding && <div className="mb-4"><TripForm primaryColor={primaryColor} onDone={() => { setAdding(false); onChanged(); }} onCancel={() => setAdding(false)} /></div>}
      {data.mileage.length === 0 ? (
        <p className="text-sm text-gray-400 italic text-center py-6">Aucun trajet pour {data.year}.</p>
      ) : (
        <ul className="divide-y" data-testid="ent-portal-km-list">
          {data.mileage.map((k) => (
            <li key={k.id} className="py-3 flex items-center gap-3" data-testid={`ent-portal-km-row-${k.id}`}>
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0"><Car className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{k.from_location} → {k.to_location}</div>
                <div className="text-xs text-gray-500">{k.trip_date}{k.purpose ? ` · ${k.purpose}` : ""}</div>
              </div>
              <div className="text-sm font-bold text-gray-900 font-mono">{k.km_distance} km</div>
              {k.source === "portal" && (
                <button onClick={() => remove(k)} className={deleteBtnCls} title="Supprimer" aria-label="Supprimer le trajet" data-testid={`ent-portal-km-delete-${k.id}`}><Trash2 className="w-4 h-4" /></button>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
